import { ValidationError } from 'api/usePhysicianEmailStatement';

export interface ParsedError {
  message: string;
  severity: 'error' | 'warning';
}

export const EMAIL_CHANGE_FALLBACK = 'An error occurred while updating the email address.';

export interface ParsedEmailChangeError extends ParsedError {
  field?: 'newEmail';
}

const STATEMENT_FALLBACK = 'An error occurred while sending the email statement.';

// The API currently returns Eastern clock time labeled as UTC.
// Formatting in UTC displays the correct Eastern time until the backend is fixed.
const timeFormat: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
};

export const formatNextAvailableTime = (nextAvailableTime: string) => {
  return new Date(nextAvailableTime).toLocaleTimeString([], timeFormat);
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const readResponse = (error: unknown): { status?: number; data: unknown } => {
  if (!isRecord(error) || !isRecord(error.response)) {
    return { data: undefined };
  }

  return {
    status: typeof error.response.status === 'number' ? error.response.status : undefined,
    data: error.response.data,
  };
};

const isRateLimitBody = (data: unknown): data is { message?: string; nextAvailableTime: string } => {
  return isRecord(data) && typeof data.nextAvailableTime === 'string' && data.nextAvailableTime.length > 0;
};

const readValidationErrors = (data: unknown): ValidationError[] | undefined => {
  if (!Array.isArray(data) || data.length === 0 || !isRecord(data[0])) {
    return undefined;
  }

  if (typeof data[0].attribute !== 'string' || typeof data[0].errorMessage !== 'string') {
    return undefined;
  }

  return data as ValidationError[];
};

export const parseEmailStatementError = (error: unknown): ParsedError => {
  const { data } = readResponse(error);

  if (isRateLimitBody(data) && data.message) {
    return {
      message: `Rate limit exceeded. Next request available at ${formatNextAvailableTime(data.nextAvailableTime)} ET.`,
      severity: 'warning',
    };
  }

  const validationErrors = readValidationErrors(data);
  if (validationErrors?.[0]?.attribute === 'email') {
    return {
      message: validationErrors[0].errorMessage,
      severity: 'error',
    };
  }

  return {
    message: STATEMENT_FALLBACK,
    severity: 'error',
  };
};

export const parseEmailChangeError = (error: unknown): ParsedEmailChangeError => {
  const { status, data } = readResponse(error);

  if (status !== undefined && status >= 500) {
    return {
      message: EMAIL_CHANGE_FALLBACK,
      severity: 'error',
    };
  }

  if (isRateLimitBody(data)) {
    const apiMessage = typeof data.message === 'string' ? data.message.trim() : '';

    return {
      message: apiMessage || 'Rate limit exceeded.',
      severity: 'warning',
    };
  }

  const validationErrors = readValidationErrors(data);
  if (validationErrors) {
    const emailError = validationErrors?.find(item => item?.attribute === 'newEmail') ?? validationErrors[0];

    return {
      message: emailError.errorMessage,
      severity: 'error',
      field: emailError.attribute === 'newEmail' ? 'newEmail' : undefined,
    };
  }

  if (typeof data === 'string' && data.trim()) {
    return {
      message: data.trim(),
      severity: 'error',
    };
  }

  if (isRecord(data) && typeof data.message === 'string' && data.message.trim()) {
    return {
      message: data.message.trim(),
      severity: 'error',
    };
  }

  return {
    message: EMAIL_CHANGE_FALLBACK,
    severity: 'error',
  };
};
